import React from "react"
import { graphql, PageProps } from "gatsby"
import { PageType } from '../custom-types'
import * as E from "../elementor"
import { EMAIL_CAPTURE_FOOTER_ID } from "../../constants"

// COMPONENT IMPORTS
import Layout from "../components/layout/layout"
import Hero from '../components/one-column-hero'
import HowItWorks from "../components/how-it-works/HowItWorks"
import HowItWorksCondensed from "../components/how-it-works/HowItWorksCondensed"
import Directory from "../components/directory/directory"
import ByTheNumbers from "../components/by-the-numbers/ByTheNumbers"
import FAQ from "../components/FAQ"
import SecondarySmallCTA from "../components/secondary-cta/SecondarySmallCTA"
import SimpleBanner from "../components/simple-banner"
import BlockReviews from "../components/block-reviews"
import TherapyCards from "../components/therapy-cards"
import Seo from "../components/Seo"
import ValueProps from "../components/value-props"
import HalfAndHalf from "../components/HalfAndHalf"
import AVOBBanner from "../components/avob-banner"
import InteractiveCardGroup from "../components/interactive-cards/interactive-card-group"
import LandscapeHero from "../components/landscape-hero"
import ExperimentalLargeCTA from "../components/secondary-cta/ExperimentalLargeCTA"
import SocialProofCarousel from "../components/social-proof-carousel/SocialProofCarousel"
import BlogPreview from "../components/blog-preview/BlogPreview"
import EmbeddedFormHero from "../components/experiment/EmbeddedFormHero"
import PartnerBanner from "../components/partners"
import DirectoryExperiment from "../components/directory/directory-experiment"
import HalfAndHalfPaidSocial from "../components/HalfAndHalfPaidSocial"
import HalfAndHalfHero from "../components/HalfAndHalfHero"
import Stats from "../components/stats/Stats"
import HowItWorksTofu from "../components/how-it-works/HowItWorksTofu"

const PageTemplate = ({ location, data }: PageProps<PageType>) => {
  const sections = E.load(data.page.elementorData)
  const experimentHero = sections.find(s => s.cssId === "experiment-hero")
  const halfAndHalfHero = sections.find(s => s.cssId === "half-and-half-hero")
  const partners = sections.find(s => s.cssId === "section-partners")
  const hero = sections.find(s => s.cssId === "section-hero")
  const landscapeHero = sections.find(s => s.cssId === "section-landscape-hero")
  const smallCTA = sections.find(s => s.cssId === "section-small-cta")
  const navCTA = sections.find(s => s.cssId === "section-nav-cta")
  const sectionSingleCTA = sections.find(s => s.cssId === "section-single-cta")
  const { button } = navCTA !== undefined ? E.collect(navCTA) : { button: [] }
  const emailCaptureSection = sections.find(s => s.cssId === EMAIL_CAPTURE_FOOTER_ID)
  const customNavBanner = sections.find(s => s.cssId === "section-custom-nav-banner")

  return (
    <Layout name={`patient${landscapeHero ? "-landscape-hero" : ""}`} location={location} ctaLink={button[0]?.link} ctaText={button[0]?.text} emailCaptureSection={emailCaptureSection} customNavBanner={customNavBanner}>
      {halfAndHalfHero && <HalfAndHalfHero section={halfAndHalfHero} /> }
      {experimentHero && <EmbeddedFormHero section={experimentHero} theme='amethist' />}
      {partners !== undefined && <PartnerBanner section={partners} theme="white" />}
      {hero && <Hero section={hero} />}
      {landscapeHero && <LandscapeHero section={landscapeHero} />}
      <div className={`rula-homepage-wrapper${landscapeHero ? " landscape-hero" : "" }`}>
      { sections
        .map((s: E.Section) => {
          switch (s.cssId) {
            case "section-directory":
             return <div className="directory-section-wrapper-patient"><Directory section={s} /></div>
            case "cond-carousel-WEBP3":
              return <div className="directory-section-wrapper-experiment"><DirectoryExperiment section={s} /></div>
            case 'paid-social-form':
              return <div className='form-section-wrapper-patient'><HalfAndHalfPaidSocial section={s} theme='transparent' /></div>
            case 'section-form':
              return <div className='form-section-wrapper-patient'><HalfAndHalf section={s} theme='transparent' /></div>
            case "section-banner":
              return <SimpleBanner section={s} theme="light-sky" />
            case "section-value-props":
              return <div className="value-props-section-wrapper-patient"><ValueProps section={s} /></div>
            case "avob-banner":
              return <AVOBBanner />
            case "section-how-it-works":
              if (!!landscapeHero) {
                return <div id="how-it-works" className="how-it-works-section-wrapper-patient"><HowItWorksCondensed section={s} /></div>
              } else if (!!halfAndHalfHero) {
                return <div id="how-it-works" className="how-it-works-section-wrapper-patient"><HowItWorksTofu section={s} /></div>
              } else {
                return <div id="how-it-works" className="how-it-works-section-wrapper-patient"><HowItWorks section={s} /></div>
              }
            case "section-by-the-numbers":
              return <ByTheNumbers section={s} />
            case "section-banner-dark":
              return <SimpleBanner section={s} theme="dark-fern" />
            case "section-block-reviews":
              return <div className="block-reviews-section-wrapper-patient"><BlockReviews section={s} /></div>
            case "section-therapy-cards":
              return <div className="therapy-cards-section-wrapper-patient"><TherapyCards section={s} /></div>
            case "section-faq":
              return <div className="faq-section-wrapper-patient"><FAQ section={s} /></div>
            case "section-interactive-card-group":
              return <div className="interactive-cards-section-wrapper-patient"><InteractiveCardGroup section={s} /></div>
            case "section-social-proof-carousel":
              return <div className="social-proof-carousel-section-wrapper"><SocialProofCarousel section={s} /></div>
            case "section-stats":
              return <Stats section={s} />
            case "section-blog-preview":
              return <BlogPreview section={s} slideInfo={data.page?.fromTheBlog || []} latestPosts={data.latestPosts || []} />
          }
        })
      }
      </div>

      {smallCTA !== undefined && <SecondarySmallCTA section={smallCTA} color="amethist" solidDark={false} />}
      {sectionSingleCTA && <ExperimentalLargeCTA section={sectionSingleCTA} />}
    </Layout>
  )
}

export default PageTemplate

export const Head = ({ data }) => <Seo seo={data?.page?.seo}/>
export const pageQuery = graphql`
  query PageById(
    $id: String!
  ) {
    page: wpPage(id: { eq: $id }) {
      id
      content
      elementorData
      title
      fromTheBlog {
        fieldGroupName
        slidePosts {
          ... on WpPost {
            id
            title
            excerpt
            uri
            slug
            author {
              node {
                name
              }
            }
            featuredImage {
              node {
                altText
                title
                sourceUrl
              }
            }
            modified
          }
        }
      }
      seo {
        fullHead
        metaRobotsNoindex
        metaRobotsNofollow
      }
      date(formatString: "MMMM DD, YYYY")
    },
    latestPosts: allWpPost(limit: 3, filter: { status: { eq: "publish" }}, sort: { date: DESC }) {
      nodes {
        id
        title
        excerpt
        uri
        slug
        author {
          node {
            name
          }
        }
        featuredImage {
          node {
            altText
            title
            sourceUrl
          }
        }
        modified
      }
    }
  }
`
